import React from 'react';
import { create } from "zustand";

import type { AppDefinition, StoredAppDefinition } from '../types/apps';
import { createFileLogger } from '../utils/logger';
import { lazyWithReload } from '../utils/lazyWithReload';

// Lazy-load ALL apps to prevent circular dependencies. Use `lazyWithReload`
// (not React.lazy) so a stale-chunk 404 after a deploy triggers a clean
// cache-busting reload instead of throwing `undefined is not an object
// (evaluating 'f._result.default')` into the Sentry ErrorBoundary.
const WebDocumentApp = lazyWithReload(() => import('../apps/WebDocumentApp/WebDocumentApp'));
const BrowserApp = lazyWithReload(() => import('../apps/BrowserApp/BrowserApp'));
const ChatApp = lazyWithReload(() => import('../apps/ChatApp/ChatApp'));
const RiverDriveApp = lazyWithReload(() => import('../apps/RiverDriveApp/RiverDriveApp'));
const MediaViewerApp = lazyWithReload(() => import('../apps/ImageStudioApp/MediaViewerApp').then(m => ({ default: m.MediaViewerApp })));
const PDFiumApp = lazyWithReload(() => import('../apps/PDFiumApp/PDFiumApp').then(m => ({ default: m.PDFiumApp })));
const DocsApp = lazyWithReload(() => import('../apps/DocsApp/components/DocsApp'));
const CanvasApp_v2 = lazyWithReload(() => import('../apps/CanvasApp_v2/CanvasApp_v2')); // ✅ NEW: v2 Canvas app
const SheetsApp_v2 = lazyWithReload(() => import('../apps/SheetsApp_v2/SheetsApp_v2')); // ✅ NEW: v2 Sheets app
const PresentationsApp_v2 = lazyWithReload(() => import('../apps/PresentationsApp_v2/PresentationsApp_v2')); // ✅ NEW: v2 Presentations app
const NotesApp_v2 = lazyWithReload(() => import('../apps/NotesApp_v2/NotesApp_v2')); // ✅ NEW: v2 Notes app
const PageApp_v2 = lazyWithReload(() => import('../apps/PageApp_v2/PageApp_v2')); // ✅ NEW: v2 Page app
const TranscriptApp = lazyWithReload(() => import('../apps/TranscriptApp/TranscriptApp'));
const AppBuilderApp = lazyWithReload(() => import('../apps/AppBuilderApp/AppBuilderApp'));
const FileViewerApp = lazyWithReload(() => import('../apps/FileViewerApp/FileViewerApp'));
const FileEditorApp = lazyWithReload(() => import('../apps/FileEditorApp/FileEditorApp'));
const IngestWizard = lazyWithReload(() => import('../apps/IngestWizard/IngestWizard'));
const StoreApp = lazyWithReload(() => import('../apps/StoreApp/StoreApp'));
const TasksApp = lazyWithReload(() => import('../apps/TasksApp/TasksApp'));
const LegalCodeViewerApp = lazyWithReload(() => import('../apps/LegalCodeViewerApp/LegalCodeViewerApp'));
const EntityViewApp = lazyWithReload(() => import('../apps/EntityViewApp/EntityViewApp'));
const NewWindowLauncherApp = lazyWithReload(() => import('../apps/EntityViewApp/views/NewWindowView').then(m => ({ default: m.NewWindowView })));
const NeurvanaApp = lazyWithReload(() => import('../apps/NeurvanaApp/NeurvanaApp'));
const PromptLibraryApp = lazyWithReload(() => import('../apps/PromptLibraryApp/PromptLibraryApp'));
const MediaGenApp = lazyWithReload(() => import('../apps/MediaGenApp/MediaGenApp'));
const ImageStudioApp = lazyWithReload(() => import('../apps/ImageStudioApp/ImageStudioApp'));
const GoogleDriveFileApp = lazyWithReload(() => import('../apps/GoogleDriveFileApp/GoogleDriveFileApp'));
const GoogleDriveApp = lazyWithReload(() => import('../apps/GoogleDriveApp/GoogleDriveApp'));
const LocalFileApp = lazyWithReload(() => import('../apps/LocalFileApp/LocalFileApp'));
const APIConnectionApp = lazyWithReload(() => import('../apps/APIConnectionApp/APIConnectionApp').then(m => ({ default: m.APIConnectionApp })));
const SettingsApp = lazyWithReload(() => import('../apps/SettingsApp/SettingsApp').then(m => ({ default: m.SettingsApp })));
const ConnectionsApp = lazyWithReload(() => import('../apps/ConnectionsApp/ConnectionsApp').then(m => ({ default: m.ConnectionsApp })));
const BankApp = lazyWithReload(() => import('../apps/BankApp/components/BankApp'));
const QuickLauncherApp = lazyWithReload(() => import('../apps/QuickLauncherApp/QuickLauncherApp'));
const MailApp = lazyWithReload(() => import('../apps/MailApp/MailApp').then(m => ({ default: m.MailApp })));
const MailOverviewApp = lazyWithReload(() => import('../apps/MailApp/MailOverviewApp').then(m => ({ default: m.MailOverviewApp })));
const MessagingApp = lazyWithReload(() => import('../apps/MessagingApp/MessagingApp').then(m => ({ default: m.MessagingApp })));
const WritersDashboardApp_v2 = lazyWithReload(() => import('../apps/WritersDashboardApp_v2/WritersDashboardApp_v2'));
const BookFormatterApp_v2 = lazyWithReload(() => import('../apps/BookFormatterApp_v2/BookFormatterApp_v2'));
const ManuscriptReviewReportApp_v2 = lazyWithReload(() => import('../apps/ManuscriptReviewReportApp_v2/ManuscriptReviewReportApp_v2'));
const PasswordVaultApp = lazyWithReload(() => import('../apps/PasswordVaultApp/PasswordVaultApp').then(m => ({ default: m.PasswordVaultApp })));
const ContactsApp = lazyWithReload(() => import('../apps/ContactsApp/ContactsApp').then(m => ({ default: m.ContactsApp })));
const AutomationsApp = lazyWithReload(() => import('../apps/AutomationsApp/AutomationsApp'));
// Register AutomationsApp as a standalone page renderer (side effect)
import '../apps/AutomationsApp/registerStandalone';
// Register SheetsApp_v2 as a standalone page renderer (side effect)
import '../apps/SheetsApp_v2/registerStandalone';
// Register core entity app standalone renderers (side effect)
import '../apps/registerCoreStandalone';
const ListenApp = lazyWithReload(() => import('../apps/ListenApp/ListenApp'));
const BookkeeperApp = lazyWithReload(() => import('../apps/BookkeeperApp/BookkeeperApp'));
const RulesApp_v2 = lazyWithReload(() => import('../apps/RulesApp_v2/RulesApp_v2'));
const PromptsApp_v2 = lazyWithReload(() => import('../apps/PromptsApp_v2/PromptsApp_v2'));
const MemoryApp_v2 = lazyWithReload(() => import('../apps/MemoryApp_v2/MemoryApp_v2'));
const SkillsApp_v2 = lazyWithReload(() => import('../apps/SkillsApp_v2/SkillsApp_v2'));
const MarkdownApp_v2 = lazyWithReload(() => import('../apps/MarkdownApp_v2/MarkdownApp_v2'));
const TerminalApp = lazyWithReload(() => import('../apps/TerminalApp/TerminalApp'));
const DashboardApp = lazyWithReload(() => import('../apps/DashboardApp/DashboardApp'));
const EntityGraphApp = lazyWithReload(() => import('../apps/EntityGraphApp/EntityGraphApp'));
const DatabaseApp = lazyWithReload(() => import('../apps/DatabaseApp/DatabaseApp'));
const VideoStudioApp = lazyWithReload(() => import('../apps/VideoStudioApp/VideoStudioApp'));
const ChartApp = lazyWithReload(() => import('../apps/ChartApp/ChartApp'));
const SchemaEntityApp = lazyWithReload(() => import('../apps/SchemaEntityApp/SchemaEntityApp'));
const SkillWizardApp = lazyWithReload(() => import('../apps/SkillWizardApp/SkillWizardApp'));
const WorkflowApp = lazyWithReload(() => import('../apps/WorkflowApp/WorkflowApp'));
const SequenceBuilderApp = lazyWithReload(() => import('../apps/SequenceBuilderApp/SequenceBuilderApp'));
const SocialStudioApp = lazyWithReload(() => import('../apps/SocialStudioApp/SocialStudioApp'));
const DiagramApp = lazyWithReload(() => import('../apps/DiagramApp/DiagramApp'));
const NotificationsApp = lazyWithReload(() => import('../apps/NotificationsApp/NotificationsApp'));
const AdminDashboardApp = lazyWithReload(() => import('../apps/AdminDashboardApp/AdminDashboardApp'));
const ProspectApp = lazyWithReload(() => import('../apps/ProspectApp/ProspectApp'));
const SalesIntegrationsApp = lazyWithReload(() => import('../apps/SalesIntegrationsApp/SalesIntegrationsApp'));
const UpgradeApp = lazyWithReload(() => import('../apps/UpgradeApp/UpgradeApp'));
const FeedbackApp = lazyWithReload(() => import('../apps/FeedbackApp/FeedbackApp'));

// ── Games ─────────────────────────────────────────────────────────────────
const PongApp = lazyWithReload(() => import('../apps/PongApp/PongApp'));
const RapidsApp = lazyWithReload(() => import('../apps/RapidsApp/RapidsApp'));

// ── Physician widgets ─────────────────────────────────────────────────────────
const CrclApp = lazyWithReload(() => import('../apps/widgets/physicians/CrclApp/CrclApp'));
const Icd10App = lazyWithReload(() => import('../apps/widgets/physicians/Icd10App/Icd10App'));
const Icd10xApp = lazyWithReload(() => import('../apps/widgets/physicians/Icd10xApp/Icd10xApp'));
const EmApp = lazyWithReload(() => import('../apps/widgets/physicians/EmApp/EmApp'));
const DenialApp = lazyWithReload(() => import('../apps/widgets/physicians/DenialApp/DenialApp'));
const InteractionApp = lazyWithReload(() => import('../apps/widgets/physicians/InteractionApp/InteractionApp'));
const MedicationTableApp = lazyWithReload(() => import('../apps/widgets/physicians/MedicationTableApp/MedicationTableApp'));

// 🎯 PERFORMANCE: Preload functions for common apps (call early to avoid Suspense flash)
export const preloadQuickLauncher = () => {
  import('../apps/QuickLauncherApp/QuickLauncherApp').catch(() => {
    // Ignore preload errors - lazy load will retry when actually needed
  });
};

export const preloadDocsApp = () => {
  import('../apps/DocsApp/components/DocsApp').catch(() => {
    // Ignore preload errors - lazy load will retry when actually needed
  });
};

// Chunk loaders for the handful of apps reachable directly from the create
// palette / "New Window" launcher gallery — the cold first-open path users hit
// most often right after landing in a space. Keyed so hover-triggered preload
// (see preloadAppChunk below) can warm the exact chunk a button will open.
const CREATE_PALETTE_APP_LOADERS: Record<string, () => Promise<unknown>> = {
  'new-window': () => import('../apps/EntityViewApp/views/NewWindowView'),
  docs: () => import('../apps/DocsApp/components/DocsApp'),
  'sheets-v2': () => import('../apps/SheetsApp_v2/SheetsApp_v2'),
  'notes-v2': () => import('../apps/NotesApp_v2/NotesApp_v2'),
  'page-v2': () => import('../apps/PageApp_v2/PageApp_v2'),
  'presentations-v2': () => import('../apps/PresentationsApp_v2/PresentationsApp_v2'),
  'canvas-v2': () => import('../apps/CanvasApp_v2/CanvasApp_v2'),
};

/** Warm a single create-palette app's chunk on demand (e.g. on button hover). */
export const preloadAppChunk = (appType: string) => {
  CREATE_PALETTE_APP_LOADERS[appType]?.().catch(() => {
    // Ignore preload errors - lazy load will retry when actually needed
  });
};

// CreateMenuItem ids (see useCreateMenuConfig) → appType, so hovering a "New X"
// row in either create surface (SlimCreatePalette, sidebar CreateMenu) warms
// that app's chunk before the click.
const CREATE_ITEM_APP_TYPES: Record<string, string> = {
  doc: 'docs',
  note: 'notes-v2',
  sheet: 'sheets-v2',
  slides: 'presentations-v2',
  canvas: 'canvas-v2',
  page: 'page-v2',
};

/** Warm the chunk for a CreateMenuItem id (e.g. on row hover). No-op for ids without a mapped app. */
export const preloadCreateMenuItem = (itemId: string) => {
  const appType = CREATE_ITEM_APP_TYPES[itemId];
  if (appType) preloadAppChunk(appType);
};

export const preloadCommonApps = () => {
  // Preload only the two apps virtually every session opens. The rest of the
  // create-palette gallery (Sheets, Notes, Page, Presentations, Canvas, "New
  // Window") is warmed on demand instead — see preloadAppChunk/preloadCreateMenuItem,
  // which already fire on hover for those entry points.
  preloadDocsApp();
  import('../apps/ChatApp/ChatApp').catch(() => {});
};

// ✅ FIX: All apps are now lazy-loaded React components (consistent with QuickLauncherApp pattern)
// This breaks circular dependencies and prevents "Cannot access 'X' before initialization" errors
const componentMap: Record<string, React.ComponentType<any>> = {
  pdfium: PDFiumApp,
  entityBrowser: RiverDriveApp,
  entityBrowser2: RiverDriveApp,
  webdocument: WebDocumentApp,
  browser: BrowserApp,
  chat: ChatApp,
  imagestudio: MediaViewerApp,
  mediaviewer: MediaViewerApp, // backward-compat alias for persisted window state
  docs: DocsApp,
  'canvas-v2': CanvasApp_v2, // ✅ NEW: v2 Canvas app
  'sheets-v2': SheetsApp_v2, // ✅ NEW: v2 Sheets app
  'presentations-v2': PresentationsApp_v2, // ✅ NEW: v2 Presentations app
  'notes-v2': NotesApp_v2, // ✅ NEW: v2 Notes app
  'page-v2': PageApp_v2, // ✅ NEW: v2 Page app
  'transcript': TranscriptApp,
  'file-viewer': FileEditorApp, // backward-compat alias → FileEditorApp
  'file-editor': FileEditorApp,
  'app-builder': AppBuilderApp, // ✅ NEW: App Builder IDE
  bank: BankApp,
  ingestWizard: IngestWizard,
  store: StoreApp,
  tasks: TasksApp,
  quicklauncher: QuickLauncherApp,
  'legal-code-viewer': LegalCodeViewerApp,
  'entity-view': EntityViewApp,
  'new-window': NewWindowLauncherApp,
  neurvana: NeurvanaApp,
  'prompt-library': PromptLibraryApp,
  mediagen: MediaGenApp,
  'media-gen': MediaGenApp, // Legacy alias for persisted windows
  'image-studio': ImageStudioApp,
  'google-drive-file': GoogleDriveFileApp,
  'drive': GoogleDriveApp,
  'local-file': LocalFileApp,
  'api-connections': APIConnectionApp,
  'connections': ConnectionsApp,
  settings: SettingsApp,
  canvas: CanvasApp_v2, // Legacy alias for persisted windows (v1 CanvasApp component was removed)
  mail: MailApp,
  'mail-app': MailApp, // alias used by entityTypeMapping for Mail.Thread / Mail.Draft routing
  'mail-overview': MailOverviewApp, // gallery of Mail.Thread entities
  messaging: MessagingApp,
  'messaging-app': MessagingApp,
  writers_dashboard: WritersDashboardApp_v2,
  book_formatter_v2: BookFormatterApp_v2,
  'manuscript-review-report': ManuscriptReviewReportApp_v2,
  'password-vault': PasswordVaultApp,
  contacts: ContactsApp,
  automations: AutomationsApp,
  goals: AutomationsApp, // legacy alias — "GoalsApp" windows rendered by AutomationsApp
  agent: AutomationsApp, // legacy alias — "AgentApp" windows rendered by AutomationsApp
  'listen-app': ListenApp,
  bookkeeper: BookkeeperApp,
  rules: RulesApp_v2,
  'prompts-v2': PromptsApp_v2,
  'memory-v2': MemoryApp_v2,
  'skills-v2': SkillsApp_v2,
  'markdown-v2': MarkdownApp_v2,
  terminal: TerminalApp,
  dashboard: DashboardApp,
  'entity-graph': EntityGraphApp,
  database: DatabaseApp,
  videostudio: VideoStudioApp,
  chart: ChartApp,
  // Every Generic Template Entity Kit type renders through the single
  // SchemaEntityApp. 'character'/'equipment' kept as aliases for any persisted
  // window state that still carries the old per-type appType.
  'schema-entity': SchemaEntityApp,
  character: SchemaEntityApp,
  equipment: SchemaEntityApp,
  medtable: MedicationTableApp,
  'skill-wizard': SkillWizardApp,
  'workflow': WorkflowApp,
  'sequences': SequenceBuilderApp,
  'socialstudio': SocialStudioApp,
  diagram: DiagramApp,
  notifications: NotificationsApp,
  'admin-dashboard': AdminDashboardApp,
  'sales-integrations': SalesIntegrationsApp,
  'prospect': ProspectApp,
  upgrade: UpgradeApp,
  feedback: FeedbackApp,

  // Games
  pong: PongApp,
  rapids: RapidsApp,

  // Physician widgets
  'phys-crcl': CrclApp,
  'phys-icd10': Icd10App,
  'phys-icd10x': Icd10xApp,
  'phys-em': EmApp,
  'phys-denial': DenialApp,
  'phys-interactions': InteractionApp,
  // Medication Table Creator: 'medtable' is the entity-backed appType (routed via
  // entityTypeRegistry); 'phys-medtable' is the launcher/dock alias that opens the
  // template gallery when no entity is supplied.
  'phys-medtable': MedicationTableApp,
};

interface AppRegistryState {
  apps: Record<string, StoredAppDefinition>;
  componentMap: Record<string, React.ComponentType<any>>;
  appsInitialized: boolean;
  initializeApps: () => Promise<void>;
}

export const useAppRegistryStore = create<AppRegistryState>((set, get) => ({
  apps: {},
  componentMap,
  appsInitialized: false,
  initializeApps: async () => {
    const log = createFileLogger('APP_REGISTRY');
    if (get().appsInitialized) {
      log.info('[APP_REGISTRY] Already initialized, skipping');
      return;
    }
    try {
      // Use dynamic import to avoid circular dependency
      const { appRegistry } = await import('../services/appRegistryService');
      const installedApps = await appRegistry.getInstalledApps();

      const formattedApps = installedApps.reduce<Record<string, StoredAppDefinition>>((acc, app: AppDefinition) => {
        acc[app.appId] = {
          _id: '', // Required by StoredAppDefinition
          entityType: 'App',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          skeleton: app,
        };
        return acc;
      }, {});

      set({ apps: formattedApps, appsInitialized: true });
    } catch (error) {
      log.error('Failed to initialize apps:', error);
    }
  },
}));
